import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Clock3, CheckCircle2, Globe2, Crown } from "lucide-react";
import { SparkBars, DonutRing } from "@/components/MiniChart";

const METRICS = [
  {
    icon: Clock3,
    value: 48,
    suffix: "Hour",
    label: "Delivery Window",
    sub: "Shortlist on your desk — guaranteed.",
    accent: "green",
    color: "#D9F99D",
    chart: "bars",
    data: [6, 14, 9, 22, 16, 28, 20, 32, 26, 40, 34, 48],
  },
  {
    icon: CheckCircle2,
    value: 97.8,
    suffix: "%",
    decimals: 1,
    label: "Probation Success",
    sub: "Hires that stay and perform.",
    accent: "purple",
    color: "#A78BFA",
    chart: "donut",
    donutValue: 97.8,
  },
  {
    icon: Globe2,
    value: 28,
    suffix: "+ Markets",
    label: "Global Talent Network",
    sub: "Across Global Financial Hubs.",
    accent: "blue",
    color: "#60A5FA",
    chart: "bars",
    data: [4, 7, 10, 12, 15, 18, 20, 22, 24, 25, 27, 28],
  },
  {
    icon: Crown,
    text: "C-Level",
    label: "Executive Search",
    sub: "Board, C-suite, VP & Director.",
    accent: "yellow",
    color: "#FDE68A",
    chart: "donut",
    donutValue: 86,
  },
];

function AnimatedNumber({ target, decimals = 0, duration = 1400 }) {
  // Always shows the target value. On first scroll-into-view we briefly
  // count up for premium effect, but the resting value is always = target,
  // so the number is never displayed mid-transition on initial paint.
  const [n, setN] = useState(target);
  const hasRun = useRef(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15%" });
  useEffect(() => {
    if (!inView || hasRun.current) return;
    hasRun.current = true;
    let raf;
    const start = performance.now();
    const from = target * 0.62; // count up only the last ~38% — feels alive
    setN(from);
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(from + (target - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, duration]);
  return <span ref={ref}>{Math.max(0, n).toFixed(decimals)}</span>;
}

export default function TrustMetrics() {
  return (
    <section
      data-testid="trust-metrics"
      className="relative"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-24 lg:py-28">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {METRICS.map((m, i) => (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.65, delay: i * 0.09 }}
              data-testid={`trust-metric-${i}`}
              data-accent={m.accent}
              className="zx-card p-6 group relative overflow-hidden"
            >
              {/* soft accent blob */}
              <div
                aria-hidden
                className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-50"
                style={{ background: `radial-gradient(circle, ${m.color}30, transparent 70%)`, filter: "blur(18px)" }}
              />
              <div className="relative flex items-center justify-between">
                <div
                  className="h-10 w-10 rounded-xl flex items-center justify-center border"
                  style={{ background: `${m.color}10`, borderColor: `${m.color}30`, color: m.color }}
                >
                  <m.icon size={18} />
                </div>
                <div className="text-[10px] font-mono uppercase tracking-[0.22em]" style={{ color: m.color }}>
                  {m.label}
                </div>
              </div>

              <div className="relative mt-5 flex items-baseline gap-2 font-mono text-white">
                {m.text ? (
                  <span className="text-4xl sm:text-5xl font-semibold tracking-tight text-white">
                    {m.text}
                  </span>
                ) : (
                  <>
                    <span
                      className="text-4xl sm:text-5xl font-semibold tracking-tight"
                      style={{ color: "#fff" }}
                    >
                      <AnimatedNumber target={m.value} decimals={m.decimals || 0} />
                    </span>
                    {m.suffix && (
                      <span className="text-sm sm:text-base text-neutral-200">{m.suffix}</span>
                    )}
                  </>
                )}
              </div>
              <p className="relative mt-2 text-sm text-neutral-200">{m.sub}</p>

              <div className="relative mt-5 flex items-end justify-between gap-3">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">
                  {m.chart === "donut" ? "Index" : "Last 12m"}
                </div>
                <div className="opacity-90 group-hover:opacity-100 transition-opacity">
                  {m.chart === "bars" ? (
                    <SparkBars values={m.data} color={m.color} />
                  ) : (
                    <DonutRing value={m.donutValue} color={m.color} size={56} />
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
